import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Menu,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  MoreVert as MoreIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
} from '@mui/icons-material';
import api from '../utils/api';

const SCHEDULE_TYPES = {
  CRON: 'cron',
  INTERVAL: 'interval',
};

const NOTIFICATION_TYPES = {
  EMAIL: 'email',
  TELEGRAM: 'telegram',
};

const SchedulesPage = () => {
  // States
  const [schedules, setSchedules] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    pipeline_id: '',
    schedule_type: SCHEDULE_TYPES.CRON,
    cron_expression: '0 9 * * 1', // Default: Every Monday at 9 AM
    interval_minutes: 60,
    is_active: true,
    notifications: [],
  });
  
  const [notificationForm, setNotificationForm] = useState({
    type: NOTIFICATION_TYPES.EMAIL,
    target: '',
    on_success: true,
    on_failure: true,
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [schedulesResponse, pipelinesResponse] = await Promise.all([
        api.get('/schedules/'),
        api.get('/pipelines/'),
      ]);
      setSchedules(schedulesResponse.data);
      setPipelines(pipelinesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading data',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationFormChange = (field, value) => {
    setNotificationForm(prev => ({ ...prev, [field]: value }));
  };

  // Add notification
  const addNotification = () => {
    if (!notificationForm.target) return;
    
    setFormData(prev => ({
      ...prev,
      notifications: [...prev.notifications, { ...notificationForm, id: Date.now() }]
    }));
    
    setNotificationForm({
      type: NOTIFICATION_TYPES.EMAIL,
      target: '',
      on_success: true,
      on_failure: true,
    });
  };

  // Remove notification
  const removeNotification = (id) => {
    setFormData(prev => ({
      ...prev,
      notifications: prev.notifications.filter(n => n.id !== id)
    }));
  };

  // Handle dialog open/close
  const openDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        name: schedule.name,
        pipeline_id: schedule.pipeline_id,
        schedule_type: schedule.schedule_type,
        cron_expression: schedule.cron_expression || '0 9 * * 1',
        interval_minutes: schedule.interval_minutes || 60,
        is_active: schedule.is_active,
        notifications: schedule.notifications || [],
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        name: '',
        pipeline_id: '',
        schedule_type: SCHEDULE_TYPES.CRON,
        cron_expression: '0 9 * * 1',
        interval_minutes: 60,
        is_active: true,
        notifications: [],
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingSchedule(null);
  };

  // Handle save
  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        schedule_expression: formData.schedule_type === SCHEDULE_TYPES.CRON 
          ? formData.cron_expression 
          : `*/${formData.interval_minutes} * * * *`
      };

      if (editingSchedule) {
        await api.put(`/schedules/${editingSchedule.id}`, payload);
        setSnackbar({
          open: true,
          message: 'Schedule updated successfully',
          severity: 'success',
        });
      } else {
        await api.post('/schedules/', payload);
        setSnackbar({
          open: true,
          message: 'Schedule created successfully',
          severity: 'success',
        });
      }
      
      fetchData();
      closeDialog();
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSnackbar({
        open: true,
        message: 'Error saving schedule',
        severity: 'error',
      });
    }
  };

  // Handle delete
  const handleDelete = async (schedule) => {
    try {
      await api.delete(`/schedules/${schedule.id}`);
      setSnackbar({
        open: true,
        message: 'Schedule deleted successfully',
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting schedule',
        severity: 'error',
      });
    }
  };

  // Handle toggle active
  const handleToggleActive = async (schedule) => {
    try {
      await api.patch(`/schedules/${schedule.id}`, {
        is_active: !schedule.is_active
      });
      setSnackbar({
        open: true,
        message: `Schedule ${schedule.is_active ? 'paused' : 'activated'} successfully`,
        severity: 'success',
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling schedule:', error);
      setSnackbar({
        open: true,
        message: 'Error updating schedule',
        severity: 'error',
      });
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, schedule) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedSchedule(schedule);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedSchedule(null);
  };

  // Get pipeline name
  const getPipelineName = (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    return pipeline ? pipeline.name : 'Unknown Pipeline';
  };

  // Format schedule expression
  const formatScheduleExpression = (schedule) => {
    if (schedule.schedule_type === SCHEDULE_TYPES.CRON) {
      return `Cron: ${schedule.cron_expression}`;
    } else {
      return `Every ${schedule.interval_minutes} minutes`;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Schedules</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => openDialog()}
        >
          Create Schedule
        </Button>
      </Box>

      {schedules.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ScheduleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No schedules configured
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first schedule to automate pipeline execution
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openDialog()}
          >
            Create Schedule
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Notifications</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {schedule.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getPipelineName(schedule.pipeline_id)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatScheduleExpression(schedule)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={schedule.is_active ? 'Active' : 'Paused'}
                      size="small"
                      color={schedule.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {schedule.notifications?.map((notification, index) => (
                        <Tooltip key={index} title={`${notification.type}: ${notification.target}`}>
                          <Chip
                            size="small"
                            icon={notification.type === 'email' ? <EmailIcon /> : <TelegramIcon />}
                            label={notification.type}
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                      {(!schedule.notifications || schedule.notifications.length === 0) && (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {schedule.next_run_at 
                        ? new Date(schedule.next_run_at).toLocaleString()
                        : 'Not scheduled'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(schedule)}
                      color={schedule.is_active ? 'warning' : 'success'}
                    >
                      {schedule.is_active ? <PauseIcon /> : <RunIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, schedule)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          openDialog(selectedSchedule);
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          handleDelete(selectedSchedule);
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSchedule ? 'Edit Schedule' : 'Create Schedule'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Schedule Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Pipeline</InputLabel>
                <Select
                  value={formData.pipeline_id}
                  onChange={(e) => handleFormChange('pipeline_id', e.target.value)}
                  label="Pipeline"
                >
                  {pipelines.map((pipeline) => (
                    <MenuItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={formData.schedule_type}
                  onChange={(e) => handleFormChange('schedule_type', e.target.value)}
                  label="Schedule Type"
                >
                  <MenuItem value={SCHEDULE_TYPES.CRON}>Cron Expression</MenuItem>
                  <MenuItem value={SCHEDULE_TYPES.INTERVAL}>Interval</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {formData.schedule_type === SCHEDULE_TYPES.CRON ? (
                <TextField
                  label="Cron Expression"
                  value={formData.cron_expression}
                  onChange={(e) => handleFormChange('cron_expression', e.target.value)}
                  fullWidth
                  helperText="e.g., '0 9 * * 1' for every Monday at 9 AM"
                />
              ) : (
                <TextField
                  label="Interval (minutes)"
                  type="number"
                  value={formData.interval_minutes}
                  onChange={(e) => handleFormChange('interval_minutes', parseInt(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Notification Type</InputLabel>
                <Select
                  value={notificationForm.type}
                  onChange={(e) => handleNotificationFormChange('type', e.target.value)}
                  label="Notification Type"
                >
                  <MenuItem value={NOTIFICATION_TYPES.EMAIL}>Email</MenuItem>
                  <MenuItem value={NOTIFICATION_TYPES.TELEGRAM}>Telegram</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={notificationForm.type === NOTIFICATION_TYPES.EMAIL ? 'Email Address' : 'Telegram Chat ID'}
                value={notificationForm.target}
                onChange={(e) => handleNotificationFormChange('target', e.target.value)}
                fullWidth
                placeholder={notificationForm.type === NOTIFICATION_TYPES.EMAIL ? 'user@example.com' : '@username or chat_id'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                onClick={addNotification}
                disabled={!notificationForm.target}
                fullWidth
                sx={{ height: '56px' }}
              >
                Add Notification
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationForm.on_success}
                      onChange={(e) => handleNotificationFormChange('on_success', e.target.checked)}
                    />
                  }
                  label="Notify on Success"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationForm.on_failure}
                      onChange={(e) => handleNotificationFormChange('on_failure', e.target.checked)}
                    />
                  }
                  label="Notify on Failure"
                />
              </Box>
            </Grid>
            
            {formData.notifications.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Configured Notifications:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.notifications.map((notification) => (
                    <Chip
                      key={notification.id}
                      icon={notification.type === 'email' ? <EmailIcon /> : <TelegramIcon />}
                      label={`${notification.target} (${notification.on_success ? 'Success' : ''}${notification.on_success && notification.on_failure ? ', ' : ''}${notification.on_failure ? 'Failure' : ''})`}
                      onDelete={() => removeNotification(notification.id)}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name || !formData.pipeline_id}
          >
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulesPage;
